import React, { useEffect } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	ModalProps,
	useBreakpointValue,
} from 'src/components/ui';

interface CustomModalProps extends ModalProps {
	header?: React.ReactNode;
	children: React.ReactNode;
	footer?: React.ReactNode;
	isTemplateModal?: boolean;
	modalRef?: React.RefObject<HTMLDivElement>;
	withBorder?: boolean;
	height?: string;
	minWidth?: string;
	headerBgColor?: string;
	headerTextColor?: string;
	withoutCloseButton?: boolean;
	headerBgImage?: string;
	width?: string;
	maxHeight?: string;
	stickyFooter?: boolean;
}

const CustomModal: React.FC<CustomModalProps> = ({
	isOpen,
	onClose,
	header,
	children,
	footer,
	isTemplateModal = false,
	modalRef,
	withBorder = true,
	height,
	minWidth,
	headerBgColor,
	headerTextColor,
	withoutCloseButton,
	headerBgImage,
	width,
	maxHeight,
	stickyFooter,
	size,
	...props
}) => {
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}
		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [isOpen]);

	const isMobile = window.screen.width <= 768;

	// Always ensure there's a maximum width so modals never take full screen
	// IMPORTANT: useBreakpointValue must be called at the top level, not inside a function
	const modalMaxWidth = useBreakpointValue({
		base: '95vw',
		sm: '90vw',
		md: '85vw',
		lg: '80vw',
		xl: '75vw',
	});

	const calculateMaxHeight = () => {
		if (height) return height;

		if (maxHeight) return maxHeight;

		if (isTemplateModal) return isMobile ? '45vh' : '90vh';

		return '95vh';
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={withoutCloseButton ? () => {} : onClose}
			blockScrollOnMount={false}
			autoFocus={false}
			trapFocus={false}
			{...props}
		>
			<ModalOverlay zIndex={10500} />
			<ModalContent
				ref={modalRef}
				margin="auto"
				style={{ overflow: 'hidden' }}
				maxWidth={modalMaxWidth}
				width={size}
				minWidth={minWidth}
				maxH={calculateMaxHeight()}
				{...(isTemplateModal && {
					minH: isMobile ? '45vh' : '90vh',
					width: '80%',
					borderRadius: 'md',
				})}
				containerProps={{
					zIndex: 10500,
				}}
				display="flex"
				flexDirection="column"
			>
				<ModalHeader
					pb={isTemplateModal ? 2 : undefined}
					bg={headerBgColor}
					color={headerTextColor}
					backgroundImage={headerBgImage ? `url(${headerBgImage})` : undefined}
					backgroundSize="cover"
					backgroundPosition="center"
				>
					{header}
				</ModalHeader>

				{!withoutCloseButton && (
					<ModalCloseButton color={headerTextColor} size="lg" top={2} right={2} />
				)}

				<ModalBody
					overflowY="auto"
					flex="1"
					borderWidth={withBorder ? '1px' : '0px'}
					minH={0}
				>
					{children}
				</ModalBody>

				{footer != null && (
					<ModalFooter
						position="sticky"
						bottom={0}
						background="#fff"
						zIndex={2}
						boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
						flexShrink={0}
					>
						{footer}
					</ModalFooter>
				)}
			</ModalContent>
		</Modal>
	);
};

export default CustomModal;
